/**
 * Users Analytics Table - Filterable user data table
 *
 * Shows all users with key metrics: votes, prompts, models seen, etc.
 * Supports filtering by column values and sorting.
 * Click on a user row to see detailed analytics.
 */

import { useCallback, useEffect, useState } from 'react';

import { apiRequest } from '@/config';
import { logError } from '@/lib/logger';

import { LoadingState } from '../ui/EmptyState';
import {
  Table,
  TableBody,
  TableCell,
  TableEmptyState,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '../ui/Table';
import { UserDetailModal } from './UserDetailModal';

// Types
interface UserAnalytics {
  email: string;
  votes: number;
  prompts: number;
  modelsSeen: number;
  firstVote: string;
  lastVote: string;
  sources: string[];
}

interface Summary {
  totalUsers: number;
  totalVotes: number;
  avgVotesPerUser: number;
  medianVotesPerUser: number;
  top10PercentContribution: number;
}

interface UsersAnalyticsResponse {
  success: boolean;
  users: UserAnalytics[];
  total: number;
  summary: Summary;
}

type SortField =
  | 'votes'
  | 'prompts'
  | 'models_seen'
  | 'first_vote'
  | 'last_vote'
  | 'email';
type SortOrder = 'asc' | 'desc';

interface Filters {
  minVotes: string;
  maxVotes: string;
  minPrompts: string;
  minModelsSeen: string;
  source: '' | 'arena' | 'seed';
  startDate: string;
  endDate: string;
}

// Utility functions
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// Components
function StatCard({
  label,
  value,
  subtext,
}: {
  label: string;
  value: string | number;
  subtext?: string;
}) {
  return (
    <div className="bg-white border border-[#D1C9C4] rounded-lg p-4">
      <div className="text-sm text-text-light mb-1">{label}</div>
      <div className="text-2xl font-semibold text-text">{value}</div>
      {subtext && <div className="text-xs text-text-light mt-1">{subtext}</div>}
    </div>
  );
}

function FilterInput({
  label,
  value,
  onChange,
  placeholder,
  type = 'number',
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: 'number' | 'text';
}) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs text-text-light font-medium">{label}</label>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="px-3 py-2 border border-[#D1C9C4] rounded text-sm focus:outline-none focus:border-accent w-24"
        min={type === 'number' ? 0 : undefined}
      />
    </div>
  );
}

function SourceSelect({
  value,
  onChange,
}: {
  value: '' | 'arena' | 'seed';
  onChange: (value: '' | 'arena' | 'seed') => void;
}) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs text-text-light font-medium">Source</label>
      <select
        value={value}
        onChange={e => onChange(e.target.value as '' | 'arena' | 'seed')}
        className="px-3 py-2 border border-[#D1C9C4] rounded text-sm focus:outline-none focus:border-accent bg-white"
      >
        <option value="">All Sources</option>
        <option value="arena">Arena Only</option>
        <option value="seed">Seed Only</option>
      </select>
    </div>
  );
}

function SortIcon({ active, order }: { active: boolean; order: SortOrder }) {
  return (
    <span
      className={`ml-1 inline-block text-xs ${active ? 'text-accent' : 'text-text-light'}`}
    >
      {order === 'asc' ? '▲' : '▼'}
    </span>
  );
}

function SortableHeader({
  label,
  field,
  currentField,
  currentOrder,
  onSort,
  align = 'left',
}: {
  label: string;
  field: SortField;
  currentField: SortField;
  currentOrder: SortOrder;
  onSort: (field: SortField) => void;
  align?: 'left' | 'center' | 'right';
}) {
  const isActive = currentField === field;

  return (
    <TableHeaderCell align={align}>
      <button
        onClick={() => onSort(field)}
        className="flex items-center gap-1 hover:text-accent transition-colors w-full justify-start"
        style={{
          justifyContent:
            align === 'right'
              ? 'flex-end'
              : align === 'center'
                ? 'center'
                : 'flex-start',
        }}
      >
        {label}
        <SortIcon active={isActive} order={isActive ? currentOrder : 'desc'} />
      </button>
    </TableHeaderCell>
  );
}

function Pagination({
  total,
  limit,
  offset,
  onPageChange,
}: {
  total: number;
  limit: number;
  offset: number;
  onPageChange: (newOffset: number) => void;
}) {
  const currentPage = Math.floor(offset / limit) + 1;
  const totalPages = Math.ceil(total / limit);

  if (totalPages <= 1) return null;

  return (
    <div className="flex items-center justify-between mt-4">
      <div className="text-sm text-text-light">
        Showing {offset + 1}-{Math.min(offset + limit, total)} of {total} users
      </div>
      <div className="flex gap-2">
        <button
          onClick={() => onPageChange(Math.max(0, offset - limit))}
          disabled={offset === 0}
          className="px-3 py-1 text-sm border border-[#D1C9C4] rounded hover:bg-cream disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Previous
        </button>
        <span className="px-3 py-1 text-sm text-text-light">
          Page {currentPage} of {totalPages}
        </span>
        <button
          onClick={() => onPageChange(offset + limit)}
          disabled={offset + limit >= total}
          className="px-3 py-1 text-sm border border-[#D1C9C4] rounded hover:bg-cream disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Next
        </button>
      </div>
    </div>
  );
}

// Main Component
export function UsersAnalyticsTable() {
  const [data, setData] = useState<UsersAnalyticsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Selected user for detail modal
  const [selectedUserEmail, setSelectedUserEmail] = useState<string | null>(
    null
  );

  // Filters
  const [filters, setFilters] = useState<Filters>({
    minVotes: '',
    maxVotes: '',
    minPrompts: '',
    minModelsSeen: '',
    source: '',
    startDate: '',
    endDate: '',
  });

  // Sorting
  const [sortField, setSortField] = useState<SortField>('votes');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  // Pagination
  const [offset, setOffset] = useState(0);
  const limit = 50;

  // Debounced filter values
  const [debouncedFilters, setDebouncedFilters] = useState(filters);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilters(filters);
      setOffset(0); // Reset to first page when filters change
    }, 300);
    return () => clearTimeout(timer);
  }, [filters]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();

      if (debouncedFilters.minVotes) {
        params.set('minVotes', debouncedFilters.minVotes);
      }
      if (debouncedFilters.maxVotes) {
        params.set('maxVotes', debouncedFilters.maxVotes);
      }
      if (debouncedFilters.minPrompts) {
        params.set('minPrompts', debouncedFilters.minPrompts);
      }
      if (debouncedFilters.minModelsSeen) {
        params.set('minModelsSeen', debouncedFilters.minModelsSeen);
      }
      if (debouncedFilters.source) {
        params.set('source', debouncedFilters.source);
      }
      if (debouncedFilters.startDate) {
        params.set('startDate', debouncedFilters.startDate);
      }
      if (debouncedFilters.endDate) {
        params.set('endDate', debouncedFilters.endDate);
      }

      params.set('sortBy', sortField);
      params.set('sortOrder', sortOrder);
      params.set('limit', String(limit));
      params.set('offset', String(offset));

      const queryString = params.toString();
      const url = `/api/admin/users-analytics${queryString ? `?${queryString}` : ''}`;

      const response: UsersAnalyticsResponse = await apiRequest(url);
      setData(response);
    } catch (e: unknown) {
      const errorMessage =
        e instanceof Error ? e.message : 'Failed to load data';
      logError(
        'Failed to load users analytics',
        e instanceof Error ? e : undefined
      );
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [debouncedFilters, sortField, sortOrder, offset]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortOrder(prev => (prev === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  const updateFilter = <K extends keyof Filters>(key: K, value: Filters[K]) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      minVotes: '',
      maxVotes: '',
      minPrompts: '',
      minModelsSeen: '',
      source: '',
      startDate: '',
      endDate: '',
    });
  };

  const hasFilters = Object.values(filters).some(v => v !== '');

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      {data?.summary && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <StatCard
            label="Users (filtered)"
            value={data.total.toLocaleString()}
            subtext={`of ${data.summary.totalUsers} total`}
          />
          <StatCard
            label="Total Votes"
            value={data.summary.totalVotes.toLocaleString()}
          />
          <StatCard
            label="Avg Votes/User"
            value={data.summary.avgVotesPerUser.toFixed(1)}
          />
          <StatCard
            label="Median Votes"
            value={data.summary.medianVotesPerUser}
          />
          <StatCard
            label="Top 10% Share"
            value={`${data.summary.top10PercentContribution}%`}
            subtext="of all votes"
          />
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border border-[#D1C9C4] rounded-lg p-4">
        <div className="flex flex-wrap items-end gap-4">
          <FilterInput
            label="Min Votes ≥"
            value={filters.minVotes}
            onChange={v => updateFilter('minVotes', v)}
            placeholder="0"
          />
          <FilterInput
            label="Max Votes ≤"
            value={filters.maxVotes}
            onChange={v => updateFilter('maxVotes', v)}
            placeholder="∞"
          />
          <FilterInput
            label="Min Prompts ≥"
            value={filters.minPrompts}
            onChange={v => updateFilter('minPrompts', v)}
            placeholder="0"
          />
          <FilterInput
            label="Min Models ≥"
            value={filters.minModelsSeen}
            onChange={v => updateFilter('minModelsSeen', v)}
            placeholder="0"
          />
          <SourceSelect
            value={filters.source}
            onChange={v => updateFilter('source', v)}
          />

          {/* Date Filters */}
          <div className="flex flex-col gap-1">
            <label className="text-xs text-text-light font-medium">
              From Date
            </label>
            <input
              type="date"
              value={filters.startDate}
              onChange={e => updateFilter('startDate', e.target.value)}
              className="px-3 py-2 border border-[#D1C9C4] rounded text-sm focus:outline-none focus:border-accent"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-text-light font-medium">
              To Date
            </label>
            <input
              type="date"
              value={filters.endDate}
              onChange={e => updateFilter('endDate', e.target.value)}
              className="px-3 py-2 border border-[#D1C9C4] rounded text-sm focus:outline-none focus:border-accent"
            />
          </div>

          {hasFilters && (
            <button
              onClick={clearFilters}
              className="px-3 py-2 text-sm text-text-light hover:text-accent transition-colors"
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Loading State */}
      {loading && !data ? (
        <LoadingState message="Loading users..." />
      ) : (
        <>
          {/* Table */}
          <div className="relative">
            {loading && (
              <div className="absolute inset-0 bg-white/50 flex items-center justify-center z-10">
                <div className="text-text-light">Updating...</div>
              </div>
            )}

            <Table>
              <TableHead>
                <TableRow hover={false}>
                  <SortableHeader
                    label="Email"
                    field="email"
                    currentField={sortField}
                    currentOrder={sortOrder}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    label="Votes"
                    field="votes"
                    currentField={sortField}
                    currentOrder={sortOrder}
                    onSort={handleSort}
                    align="right"
                  />
                  <SortableHeader
                    label="Prompts"
                    field="prompts"
                    currentField={sortField}
                    currentOrder={sortOrder}
                    onSort={handleSort}
                    align="right"
                  />
                  <SortableHeader
                    label="Models Seen"
                    field="models_seen"
                    currentField={sortField}
                    currentOrder={sortOrder}
                    onSort={handleSort}
                    align="right"
                  />
                  <SortableHeader
                    label="First Vote"
                    field="first_vote"
                    currentField={sortField}
                    currentOrder={sortOrder}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    label="Last Vote"
                    field="last_vote"
                    currentField={sortField}
                    currentOrder={sortOrder}
                    onSort={handleSort}
                  />
                  <TableHeaderCell>Source</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!data?.users || data.users.length === 0 ? (
                  <TableEmptyState
                    message={
                      hasFilters
                        ? 'No users match the current filters'
                        : 'No user data available'
                    }
                    colSpan={7}
                  />
                ) : (
                  data.users.map(user => (
                    <TableRow
                      key={user.email}
                      onClick={() => setSelectedUserEmail(user.email)}
                      className="cursor-pointer"
                    >
                      <TableCell>
                        <span className="font-medium text-text hover:text-accent transition-colors">
                          {user.email}
                        </span>
                      </TableCell>
                      <TableCell align="right">
                        <span className="font-semibold text-accent">
                          {user.votes.toLocaleString()}
                        </span>
                      </TableCell>
                      <TableCell align="right">
                        <span className="text-text">
                          {user.prompts.toLocaleString()}
                        </span>
                      </TableCell>
                      <TableCell align="right">
                        <span className="text-text">{user.modelsSeen}</span>
                      </TableCell>
                      <TableCell>
                        <span className="text-text-light text-sm">
                          {formatDate(user.firstVote)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-text-light text-sm">
                          {formatDate(user.lastVote)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-xs text-text-light">
                          {user.sources.join(', ')}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

            {/* Pagination */}
            {data && (
              <Pagination
                total={data.total}
                limit={limit}
                offset={offset}
                onPageChange={setOffset}
              />
            )}
          </div>
        </>
      )}

      {/* User Detail Modal */}
      {selectedUserEmail && (
        <UserDetailModal
          email={selectedUserEmail}
          onClose={() => setSelectedUserEmail(null)}
        />
      )}
    </div>
  );
}
